import { TableCell } from '@/components/ui/table'
import { Column } from './type'
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import { Link } from 'react-router-dom'

type Props<T> = {
  column: Column<T>
  data: T
}

export const SimpleTableCellRenderer = React.memo(
  <T,>({ data, column }: Props<T>) => {
    if (column.render) {
      return (
        <TableCell
          key={column.key.toString()}
          className={classNames('h-8 py-1 text-[13px]', {
            'font-medium text-[#3561a3]': column.isPrimary,
          })}
          style={column.style}
        >
          {column.render(data)}
        </TableCell>
      )
    }

    const pending = useRef(false)
    const [promiseResult, setPromiseResult] = useState<any>()

    const value = useMemo(() => {
      const [key, ...path] = column.key.split('.')
      let _value = data[key]

      if (path) {
        for (const k of path) {
          if (!_value) {
            return undefined
          }

          _value = _value[k]
        }
      }

      return _value
    }, [data, column])

    useLayoutEffect(() => {
      if (column.formatter) {
        if (pending.current) {
          return
        }

        let _value: any | Promise<any> = value

        if (column.valueMap && typeof value === 'string') {
          _value = column.valueMap[value]
        }

        const isPromise = column.formatter(_value, value)

        if (isPromise instanceof Promise) {
          pending.current = true
          isPromise.then((v) => {
            setPromiseResult(v)
            pending.current = false
          })
        } else {
          setPromiseResult(isPromise)
        }
      }
    }, [column, data, value])

    const formattedValue = useMemo(() => {
      let _value: any | Promise<any> = value

      if (column.valueMap && typeof value === 'string') {
        _value = column.valueMap[value] ?? _value
      }

      if (column.formatter) {
        if (promiseResult) {
          _value = promiseResult
        } else {
          return
        }
      }

      if (typeof _value === 'boolean') {
        return _value ? (
          <img src="/images/check.png" className="size-3.5" />
        ) : (
          <img src="/images/x.png" className="size-3.5" />
        )
      }

      return _value
    }, [column, promiseResult, value])

    const valueContainer = useCallback(
      (value: React.ReactNode) => {
        if (column.href) {
          if (typeof column.href === 'function') {
            return (
              <Link to={column.href(data)} className="hover:underline">
                {value}
              </Link>
            )
          }

          return (
            <Link to={column.href} className="hover:underline">
              {value}
            </Link>
          )
        }

        if (column.newTab) {
          return (
            <a
              href={
                typeof column.newTab === 'function'
                  ? column.newTab(data)
                  : column.newTab
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              {value}
            </a>
          )
        }
        return value
      },
      [column, data],
    )

    return (
      <TableCell
        key={column.key.toString()}
        className={classNames('h-8 py-1 text-[13px]', {
          'font-medium text-[#3561a3]': column.isPrimary,
        })}
        style={column.style}
      >
        {valueContainer(formattedValue ?? column.fallback ?? '-')}
      </TableCell>
    )
  },
)
